<template>
    <div class="login-page">
        <header class="login-header">
            <div class="youcai-page-container login-header__inner">
                <router-link to="/" class="login-logo" aria-label="邮你采首页">
                    <img class="login-logo__image" :src="mainLogo" alt="邮你采" />
                </router-link>

                <div class="wechat-follow">
                    <div class="wechat-follow__copy">
                        <div class="wechat-follow__title">关注邮你采微信</div>
                        <div class="wechat-follow__desc">更多优惠信息、消息通知及时获取</div>
                    </div>
                    <img class="wechat-follow__qr" :src="wechatQr" alt="邮你采公众号二维码" />
                </div>
            </div>
        </header>

        <main class="login-hero">
            <img class="login-hero__banner" :src="bannerImage" alt="" />
            <div class="login-hero__inner">
                <section class="login-card" :class="{ 'login-card--qr': activeTab === 'qr' }">
                    <div class="login-tabs" role="tablist" aria-label="登录方式">
                        <button
                            v-for="tab in tabs"
                            :key="tab.name"
                            type="button"
                            class="login-tabs__item"
                            :class="{ 'login-tabs__item--active': activeTab === tab.name }"
                            :aria-selected="activeTab === tab.name"
                            role="tab"
                            @click="activeTab = tab.name"
                        >
                            {{ tab.label }}
                        </button>
                    </div>

                    <form
                        v-if="activeTab === 'account'"
                        class="login-form"
                        @submit.prevent="handleAccountLogin"
                    >
                        <input
                            v-model="accountForm.username"
                            class="login-input"
                            :maxlength="11"
                            placeholder="手机号码"
                        />
                        <input
                            v-model="accountForm.password"
                            class="login-input"
                            placeholder="密码"
                            type="password"
                        />
                        <button class="login-submit" type="submit" :disabled="submitting">
                            登录
                        </button>
                    </form>

                    <form
                        v-else-if="activeTab === 'sms'"
                        class="login-form login-form--sms"
                        @submit.prevent="handleSmsLogin"
                    >
                        <input
                            v-model="smsForm.mobile"
                            class="login-input"
                            :maxlength="11"
                            placeholder="手机号码"
                        />
                        <div class="login-input login-code-field">
                            <input
                                v-model="smsForm.code"
                                class="login-code-field__input"
                                placeholder="请输入验证码"
                            />
                            <button
                                class="login-code-field__button"
                                type="button"
                                :disabled="codeCountdown > 0 || captchaLoading"
                                @click="handleGetSmsCode"
                            >
                                {{ codeButtonText }}
                            </button>
                        </div>
                        <button class="login-submit" type="submit" :disabled="submitting">
                            登录
                        </button>
                    </form>

                    <div v-else class="qr-register">
                        <div class="qr-register__tip">
                            没有邮你采账号？请先在邮你采微信小程序上注册
                        </div>
                        <img
                            class="qr-register__image"
                            :src="registerQr"
                            alt="邮你采小程序二维码"
                        />
                        <div class="qr-register__desc">扫码打开邮你采小程序</div>
                    </div>
                </section>
            </div>
        </main>

        <SliderVerify
            v-model="captchaVisible"
            :verify-img="captchaOptions.canvasSrc"
            :verify-block="captchaOptions.blockSrc"
            :block-y="captchaOptions.blockY"
            @check="handleSliderCheck"
            @close="captchaLoading = false"
        />
    </div>
</template>

<script>
import SliderVerify from '@/components/common/SliderVerify.vue'
import { codeCaptcha, getInfo, loginByAccount, loginBySms, sendSmsCode } from '@/api/auth'
import bannerImage from '@/assets/login/banner.png'
import mainLogo from '@/assets/login/pic-logo-ync.png'
import registerQr from '@/assets/login/login-register-qr.jpg'
import wechatQr from '@/assets/login/wechat-qr.png'

const USER_STORAGE_KEY = 'youcai_user'

export default {
    name: 'LoginPage',
    components: {
        SliderVerify
    },
    data() {
        return {
            activeTab: 'account',
            submitting: false,
            tabs: [
                { label: '账户登录', name: 'account' },
                { label: '短信登录', name: 'sms' },
                { label: '扫码注册', name: 'qr' }
            ],
            accountForm: {
                username: '',
                password: ''
            },
            smsForm: {
                mobile: '',
                code: ''
            },
            captchaVisible: false,
            captchaLoading: false,
            codeCountdown: 0,
            captchaOptions: {
                canvasSrc: '',
                blockSrc: '',
                blockY: 0,
                uuid: '',
                sliderCode: 0
            },
            countdownTimer: null,
            bannerImage,
            mainLogo,
            registerQr,
            wechatQr
        }
    },
    computed: {
        codeButtonText() {
            return this.codeCountdown > 0 ? `${this.codeCountdown}秒重新获取` : '获取验证码'
        }
    },
    beforeDestroy() {
        clearInterval(this.countdownTimer)
    },
    methods: {
        redirectAfterLogin() {
            this.$router.replace(this.$route.query.redirect || '/')
        },
        saveUser(loginData, infoData) {
            const userInfo = {
                ...(loginData || {}),
                ...(infoData || {})
            }

            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userInfo))
            window.dispatchEvent(new Event('youcai-user-change'))
        },
        isValidMobile(mobile) {
            return /^1[3-9]\d{9}$/.test(mobile)
        },
        validateSmsMobile() {
            if (!this.smsForm.mobile) {
                this.$message.info('请先输入手机号码')
                return false
            }

            if (!this.isValidMobile(this.smsForm.mobile)) {
                this.$message.info('手机号码格式错误')
                return false
            }

            return true
        },
        startCountdown() {
            this.codeCountdown = 60
            clearInterval(this.countdownTimer)
            this.countdownTimer = setInterval(() => {
                this.codeCountdown -= 1
                if (this.codeCountdown <= 0) {
                    clearInterval(this.countdownTimer)
                    this.countdownTimer = null
                }
            }, 1000)
        },
        async showSliderVerify() {
            if (this.captchaLoading) return
            this.captchaLoading = true

            try {
                const res = await codeCaptcha()
                this.captchaOptions = {
                    canvasSrc: (res.data && res.data.canvasSrc) || '',
                    blockSrc: (res.data && res.data.blockSrc) || '',
                    blockY: Number((res.data && res.data.blockY) || 0),
                    uuid: (res.data && res.data.uuid) || '',
                    sliderCode: 0
                }
                this.captchaVisible = true
            } catch (error) {
                this.$message.error(error.msg || '验证码获取失败')
            } finally {
                this.captchaLoading = false
            }
        },
        handleGetSmsCode() {
            if (this.codeCountdown > 0 || this.captchaLoading) return
            if (!this.validateSmsMobile()) return
            this.showSliderVerify()
        },
        async handleSliderCheck(sliderCode) {
            this.captchaOptions.sliderCode = sliderCode

            try {
                await sendSmsCode({
                    phone: this.smsForm.mobile,
                    uuid: this.captchaOptions.uuid,
                    code: sliderCode
                })
                this.$message.success('验证码已发送')
                this.captchaVisible = false
                this.startCountdown()
            } catch (error) {
                if (String(error.code) === '510') {
                    this.captchaVisible = false
                    return
                }
                this.showSliderVerify()
            }
        },
        async handleAccountLogin() {
            if (this.submitting) return
            this.submitting = true

            try {
                if (!this.accountForm.username) {
                    this.$message.info('请先输入手机号码')
                    return
                }
                if (!this.accountForm.password) {
                    this.$message.info('请先输入密码')
                    return
                }

                const loginRes = await loginByAccount(this.accountForm)
                const loginData = loginRes.data || {}
                const token = loginData.access_token || ''

                if (!token) {
                    this.$message.error('登录失败，未返回有效凭证')
                    return
                }

                const infoRes = await getInfo({}, token)
                this.saveUser(loginData, infoRes.data || {})
                this.redirectAfterLogin()
            } catch (error) {
                this.$message.error(error.msg || '登录失败')
            } finally {
                this.submitting = false
            }
        },
        async handleSmsLogin() {
            if (this.submitting) return
            this.submitting = true

            try {
                if (!this.validateSmsMobile()) return
                if (!this.smsForm.code) {
                    this.$message.info('请输入短信验证码')
                    return
                }

                const res = await loginBySms({
                    ...this.smsForm,
                    uuid: this.captchaOptions.uuid,
                    sliderCode: this.captchaOptions.sliderCode
                })
                const loginData = res.data || {}
                const token = loginData.access_token || ''

                if (!token) {
                    this.$message.error('登录失败，未返回有效凭证')
                    return
                }

                const infoRes = await getInfo({}, token)
                this.saveUser(loginData, infoRes.data || {})
                this.redirectAfterLogin()
            } catch (error) {
                this.$message.error(error.msg || '登录失败')
            } finally {
                this.submitting = false
            }
        }
    }
}
</script>

<style scoped>
.login-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: #ffffff;
}

.youcai-page-container {
    width: 1200px;
    margin: 0 auto;
}

.login-header {
    height: 120px;
    background: #ffffff;
}

.login-header__inner {
    position: relative;
    height: 120px;
}

.login-logo {
    position: absolute;
    top: 30px;
    left: 0;
    display: block;
    width: 196px;
    height: 61px;
}

.login-logo__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.wechat-follow {
    position: absolute;
    top: 14px;
    right: 0;
    display: flex;
    width: 280px;
    height: 92px;
    align-items: flex-end;
    justify-content: space-between;
}

.wechat-follow__copy {
    width: 180px;
    margin-bottom: 6px;
    text-align: right;
}

.wechat-follow__title {
    color: #157e3b;
    font-size: 12px;
    line-height: 15px;
}

.wechat-follow__desc {
    margin-top: 11px;
    color: #000000;
    font-size: 12px;
    line-height: 15px;
}

.wechat-follow__qr {
    display: block;
    width: 92px;
    height: 92px;
}

.login-hero {
    position: relative;
    height: 480px;
    overflow: hidden;
    background: #d9d9d9;
}

.login-hero__banner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 480px;
    object-fit: cover;
}

.login-hero__inner {
    position: relative;
    height: 480px;
}

.login-card {
    position: absolute;
    top: 85px;
    right: 258px;
    box-sizing: border-box;
    width: 360px;
    height: 310px;
    border-radius: 20px;
    background: #ffffff;
    padding: 48px 32px 27px;
}

.login-card--qr {
    top: 60px;
    height: 360px;
}

.login-tabs {
    display: flex;
    width: 100%;
    height: 19px;
    align-items: center;
}

.login-tabs__item {
    border: 0;
    background: transparent;
    padding: 0;
    margin-right: 20px;
    color: #4e4e4e;
    font-size: 16px;
    font-weight: 400;
    line-height: 19px;
    outline: none;
    cursor: pointer;
}

.login-tabs__item--active {
    color: #157e3b;
    font-weight: 500;
}

.login-form {
    margin-top: 18px;
}

.login-input {
    display: block;
    box-sizing: border-box;
    width: 296px;
    height: 42px;
    border: 0;
    border-radius: 5px;
    background: #f5f5f5;
    padding: 0 16px;
    color: #333333;
    font-size: 12px;
    line-height: 42px;
    outline: none;
}

.login-input + .login-input {
    margin-top: 16px;
}

.login-input::placeholder,
.login-code-field__input::placeholder {
    color: #b3b3b3;
}

.login-submit {
    width: 296px;
    height: 42px;
    margin-top: 24px;
    border: 0;
    border-radius: 5px;
    background: #157e3b;
    color: #ffffff;
    font-size: 16px;
    line-height: 19px;
    cursor: pointer;
}

.login-submit:disabled {
    opacity: 0.7;
    cursor: default;
}

.login-form--sms .login-submit {
    margin-top: 56px;
}

.login-code-field {
    display: flex;
    align-items: center;
    padding: 0 10px 0 16px;
}

.login-code-field__input {
    min-width: 0;
    height: 100%;
    flex: 1;
    border: 0;
    background: transparent;
    color: #333333;
    font-size: 12px;
    outline: none;
}

.login-code-field__button {
    width: 90px;
    height: 32px;
    border: 1px solid #157e3b;
    border-radius: 5px;
    background: #ffffff;
    color: #157e3b;
    font-size: 12px;
    line-height: 15px;
    cursor: pointer;
}

.login-code-field__button:disabled {
    opacity: 0.65;
    cursor: default;
}

.qr-register {
    margin-top: 21px;
    text-align: center;
}

.qr-register__tip {
    width: 296px;
    height: 26px;
    border-radius: 16px;
    background: #157e3b;
    color: #ffffff;
    font-size: 12px;
    line-height: 26px;
}

.qr-register__image {
    display: block;
    width: 146px;
    height: 146px;
    margin: 23px auto 0;
}

.qr-register__desc {
    margin-top: 11px;
    color: #000000;
    font-size: 12px;
    line-height: 15px;
}

@media screen and (max-width: 768px) {
    .youcai-page-container {
        width: 100%;
        padding: 0 16px;
        box-sizing: border-box;
    }

    .login-header,
    .login-header__inner {
        height: 80px;
    }

    .login-logo {
        top: 22px;
        left: 16px;
        width: 128px;
        height: 40px;
    }

    .wechat-follow {
        display: none;
    }

    .login-card {
        right: 16px;
        left: 16px;
        width: auto;
    }

    .login-input,
    .login-submit,
    .qr-register__tip {
        width: 100%;
    }
}
</style>
